import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Terms of Service" },
      {
        name: "description",
        content:
          "The terms that govern your use of ClaimGuard, including services, payments, and acceptable use.",
      },
      { property: "og:title", content: "ClaimGuard | Terms of Service" },
      {
        property: "og:description",
        content: "The terms that govern your use of ClaimGuard.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:url", content: "https://smart-invoice-ally.lovable.app/terms" },
    ],
    links: [{ rel: "canonical", href: "https://smart-invoice-ally.lovable.app/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      updated="July 5, 2026"
      intro="These Terms of Service govern your access to and use of the ClaimGuard platform, including our invoice review, findings report, and full-service negotiation services. By creating an account or submitting an invoice, you agree to these terms."
      footer="This page is maintained by ClaimGuard. If any section conflicts with a signed agreement between you and ClaimGuard, the signed agreement controls."
      sections={[
        {
          heading: "1. The services we provide",
          body: (
            <>
              <p>
                ClaimGuard provides AI-assisted invoice review, a professional
                findings report, and — as an optional paid add-on — full-service negotiation
                support. Findings are informational tools intended to help you evaluate
                and, where appropriate, dispute invoiced charges.
              </p>
              <p>
                Availability, features, and pricing may change from time to time. We will
                give reasonable notice for material changes that affect active cases.
              </p>
            </>
          ),
        },
        {
          heading: "2. Accounts and eligibility",
          body: (
            <p>
              You must provide accurate account information and are responsible for
              activity that occurs under your account. You must be at least 18 years old
              and legally able to enter into a contract to use the services.
            </p>
          ),
        },
        {
          heading: "3. Your submissions",
          body: (
            <p>
              You retain ownership of the invoices, documents, and information you upload.
              You grant ClaimGuard a limited license to process that content for
              the purpose of delivering the services you request (review, report
              generation, negotiation, and customer support).
            </p>
          ),
        },
        {
          heading: "4. Fees, billing, and refunds",
          body: (
            <p>
              Fees for Professional AI Review and Full-Service Negotiation are shown at
              checkout and processed by our payment provider. Refund eligibility is
              described in our{" "}
              <a className="text-trust underline" href="/refund-policy">
                Refund Policy
              </a>
              .
            </p>
          ),
        },
        {
          heading: "5. Not legal advice",
          body: (
            <p>
              ClaimGuard is not a law firm and does not provide legal
              representation. Reports and negotiation communications are informational and
              intended to support your own decisions. If you need legal advice, please
              consult a licensed attorney.
            </p>
          ),
        },
        {
          heading: "6. Acceptable use",
          body: (
            <p>
              You agree not to upload content you do not have permission to share, use the
              service for unlawful purposes, attempt to reverse engineer the platform, or
              interfere with other customers' use of the service.
            </p>
          ),
        },
        {
          heading: "7. Service availability and warranties",
          body: (
            <p>
              The services are provided on an "as is" and "as available" basis. To the
              maximum extent permitted by law, ClaimGuard disclaims implied
              warranties of merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>
          ),
        },
        {
          heading: "8. Limitation of liability",
          body: (
            <p>
              To the maximum extent permitted by law, our aggregate liability for any
              claim arising out of or relating to the services is limited to the amount
              you paid to ClaimGuard in the 12 months preceding the event giving
              rise to the claim.
            </p>
          ),
        },
        {
          heading: "9. Termination",
          body: (
            <p>
              You may close your account at any time from Settings. We may suspend or
              terminate accounts that violate these terms or that we reasonably believe
              are being used to harm other customers or third parties.
            </p>
          ),
        },
        {
          heading: "10. Changes to these terms",
          body: (
            <p>
              We may update these Terms from time to time. Material changes will be
              announced in-app or by email. Continued use after the effective date
              constitutes acceptance of the updated terms.
            </p>
          ),
        },
        {
          heading: "11. Contact",
          body: (
            <p>
              Questions about these Terms? Reach us at{" "}
              <a className="text-trust underline" href="mailto:support@invoiceintelligence.com">
                support@invoiceintelligence.com
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}
